import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  TimeTrackerEntry,
  TimeTrackerProject,
  TimeTrackerClient,
  TimeTrackerTask,
} from '@/types/pm-types';

interface TimerState {
  isRunning: boolean;
  startedAt: string | null;
  description: string;
  projectId: string | null;
  taskId: string | null;
  billable: boolean;
  tags: string[];
  elapsedSeconds: number;
}

interface TimeTrackerState {
  timer: TimerState;

  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  clients: TimeTrackerClient[];
  tasks: TimeTrackerTask[];

  activeView: 'timer' | 'calendar' | 'reports' | 'invoice';
  selectedDateRange: { start: string; end: string };
  filterProjectId: string | null;
  filterClientId: string | null;
  filterBillable: boolean | null;
  filterTags: string[];

  // Timer actions
  startTimer: (opts?: { description?: string; projectId?: string; taskId?: string; billable?: boolean; tags?: string[] }) => void;
  stopTimer: () => TimeTrackerEntry | null;
  updateTimerDescription: (desc: string) => void;
  updateTimerProject: (projectId: string | null) => void;
  updateTimerTask: (taskId: string | null) => void;
  updateTimerBillable: (billable: boolean) => void;
  updateTimerTags: (tags: string[]) => void;
  tickTimer: () => void;
  discardTimer: () => void;

  // Entry actions
  addEntry: (entry: TimeTrackerEntry) => void;
  updateEntry: (id: string, updates: Partial<TimeTrackerEntry>) => void;
  deleteEntry: (id: string) => void;
  addManualEntry: (entry: Omit<TimeTrackerEntry, 'id'>) => void;

  // Project/Client/Task actions
  addProject: (project: TimeTrackerProject) => void;
  updateProject: (id: string, updates: Partial<TimeTrackerProject>) => void;
  deleteProject: (id: string) => void;
  addClient: (client: TimeTrackerClient) => void;
  updateClient: (id: string, updates: Partial<TimeTrackerClient>) => void;
  deleteClient: (id: string) => void;
  addTask: (task: TimeTrackerTask) => void;
  updateTask: (id: string, updates: Partial<TimeTrackerTask>) => void;
  deleteTask: (id: string) => void;

  // UI actions
  setActiveView: (view: TimeTrackerState['activeView']) => void;
  setDateRange: (range: { start: string; end: string }) => void;
  setFilterProjectId: (id: string | null) => void;
  setFilterClientId: (id: string | null) => void;
  setFilterBillable: (billable: boolean | null) => void;
  setFilterTags: (tags: string[]) => void;

  // Computed
  getFilteredEntries: () => TimeTrackerEntry[];
  getProjectById: (id: string) => TimeTrackerProject | undefined;
  getClientById: (id: string) => TimeTrackerClient | undefined;
  getTaskById: (id: string) => TimeTrackerTask | undefined;
  getEffectiveRate: (projectId: string, taskId?: string) => number;
  calculateBillableAmount: (entry: TimeTrackerEntry) => number;
  getTotalsByProject: (entries?: TimeTrackerEntry[]) => Record<string, { hours: number; amount: number }>;
  getTotalsByClient: (entries?: TimeTrackerEntry[]) => Record<string, { hours: number; amount: number }>;
}

const DEFAULT_TIMER: TimerState = {
  isRunning: false,
  startedAt: null,
  description: '',
  projectId: null,
  taskId: null,
  billable: false,
  tags: [],
  elapsedSeconds: 0,
};

function generateId(): string {
  return `tt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const useTimeTrackerStore = create<TimeTrackerState>()(
  subscribeWithSelector(
    immer(
      persist(
        (set, get) => ({
          timer: { ...DEFAULT_TIMER },
          entries: [],
          projects: [],
          clients: [],
          tasks: [],
          activeView: 'timer' as const,
          selectedDateRange: { start: '', end: '' },
          filterProjectId: null,
          filterClientId: null,
          filterBillable: null,
          filterTags: [],

          // ─── Timer Actions ───────────────────────────────────
          startTimer: (opts) => {
            set((state) => {
              state.timer.isRunning = true;
              state.timer.startedAt = new Date().toISOString();
              state.timer.elapsedSeconds = 0;
              if (opts?.description !== undefined) state.timer.description = opts.description;
              if (opts?.projectId !== undefined) state.timer.projectId = opts.projectId;
              if (opts?.taskId !== undefined) state.timer.taskId = opts.taskId;
              if (opts?.billable !== undefined) state.timer.billable = opts.billable;
              if (opts?.tags !== undefined) state.timer.tags = opts.tags;
            });
          },

          stopTimer: () => {
            const { timer } = get();
            if (!timer.isRunning || !timer.startedAt) return null;

            const startTime = timer.startedAt;
            const endTime = new Date().toISOString();
            const duration = Math.max(
              timer.elapsedSeconds,
              Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000)
            );
            const date = startTime.slice(0, 10);

            const entry: TimeTrackerEntry = {
              id: generateId(),
              description: timer.description,
              projectId: timer.projectId ?? undefined,
              taskId: timer.taskId ?? undefined,
              startTime,
              endTime,
              duration,
              billable: timer.billable,
              tags: [...timer.tags],
              date,
              createdAt: new Date().toISOString(),
            };

            set((state) => {
              state.entries.unshift(entry);
              state.timer = { ...DEFAULT_TIMER };
            });

            return entry;
          },

          updateTimerDescription: (desc) => set((state) => { state.timer.description = desc; }),
          updateTimerProject: (projectId) => set((state) => { state.timer.projectId = projectId; }),
          updateTimerTask: (taskId) => set((state) => { state.timer.taskId = taskId; }),
          updateTimerBillable: (billable) => set((state) => { state.timer.billable = billable; }),
          updateTimerTags: (tags) => set((state) => { state.timer.tags = tags; }),

          tickTimer: () => {
            set((state) => {
              if (state.timer.isRunning) {
                state.timer.elapsedSeconds += 1;
              }
            });
          },

          discardTimer: () => {
            set((state) => {
              state.timer = { ...DEFAULT_TIMER };
            });
          },

          // ─── Entry Actions ───────────────────────────────────
          addEntry: (entry) => set((state) => { state.entries.unshift(entry); }),

          updateEntry: (id, updates) => {
            set((state) => {
              const idx = state.entries.findIndex((e) => e.id === id);
              if (idx !== -1) {
                Object.assign(state.entries[idx], updates, { updatedAt: new Date().toISOString() });
              }
            });
          },

          deleteEntry: (id) => {
            set((state) => {
              state.entries = state.entries.filter((e) => e.id !== id);
            });
          },

          addManualEntry: (entry) => {
            const newEntry: TimeTrackerEntry = { ...entry, id: generateId(), createdAt: new Date().toISOString() };
            set((state) => { state.entries.unshift(newEntry); });
          },

          // ─── Project Actions ─────────────────────────────────
          addProject: (project) => set((state) => { state.projects.push(project); }),
          updateProject: (id, updates) => set((state) => {
            const idx = state.projects.findIndex((p) => p.id === id);
            if (idx !== -1) Object.assign(state.projects[idx], updates);
          }),
          deleteProject: (id) => set((state) => { state.projects = state.projects.filter((p) => p.id !== id); }),

          // ─── Client Actions ──────────────────────────────────
          addClient: (client) => set((state) => { state.clients.push(client); }),
          updateClient: (id, updates) => set((state) => {
            const idx = state.clients.findIndex((c) => c.id === id);
            if (idx !== -1) Object.assign(state.clients[idx], updates);
          }),
          deleteClient: (id) => set((state) => { state.clients = state.clients.filter((c) => c.id !== id); }),

          // ─── Task Actions ────────────────────────────────────
          addTask: (task) => set((state) => { state.tasks.push(task); }),
          updateTask: (id, updates) => set((state) => {
            const idx = state.tasks.findIndex((t) => t.id === id);
            if (idx !== -1) Object.assign(state.tasks[idx], updates);
          }),
          deleteTask: (id) => set((state) => { state.tasks = state.tasks.filter((t) => t.id !== id); }),

          // ─── UI Actions ──────────────────────────────────────
          setActiveView: (view) => set((state) => { state.activeView = view; }),
          setDateRange: (range) => set((state) => { state.selectedDateRange = range; }),
          setFilterProjectId: (id) => set((state) => { state.filterProjectId = id; }),
          setFilterClientId: (id) => set((state) => { state.filterClientId = id; }),
          setFilterBillable: (billable) => set((state) => { state.filterBillable = billable; }),
          setFilterTags: (tags) => set((state) => { state.filterTags = tags; }),

          // ─── Computed ────────────────────────────────────────
          getFilteredEntries: () => {
            const { entries, filterProjectId, filterClientId, filterBillable, filterTags, selectedDateRange, projects } = get();
            let filtered = entries;

            if (filterProjectId) {
              filtered = filtered.filter((e) => e.projectId === filterProjectId);
            }

            if (filterClientId) {
              const projectIds = projects.filter((p) => p.clientId === filterClientId).map((p) => p.id);
              filtered = filtered.filter((e) => e.projectId && projectIds.includes(e.projectId));
            }

            if (filterBillable !== null) {
              filtered = filtered.filter((e) => e.billable === filterBillable);
            }

            if (filterTags.length > 0) {
              filtered = filtered.filter((e) => filterTags.some((t) => e.tags.includes(t)));
            }

            if (selectedDateRange.start) {
              filtered = filtered.filter((e) => e.date >= selectedDateRange.start);
            }
            if (selectedDateRange.end) {
              filtered = filtered.filter((e) => e.date <= selectedDateRange.end);
            }

            return filtered;
          },

          getProjectById: (id) => get().projects.find((p) => p.id === id),
          getClientById: (id) => get().clients.find((c) => c.id === id),
          getTaskById: (id) => get().tasks.find((t) => t.id === id),

          getEffectiveRate: (projectId, taskId) => {
            const { projects, tasks } = get();
            if (taskId) {
              const task = tasks.find((t) => t.id === taskId);
              if (task?.billableRate !== undefined) return task.billableRate;
            }
            const project = projects.find((p) => p.id === projectId);
            return project?.billableRate ?? 0;
          },

          calculateBillableAmount: (entry) => {
            if (!entry.billable || !entry.projectId) return 0;
            const rate = get().getEffectiveRate(entry.projectId, entry.taskId);
            return (entry.duration / 3600) * rate;
          },

          getTotalsByProject: (entries) => {
            const source = entries ?? get().getFilteredEntries();
            const totals: Record<string, { hours: number; amount: number }> = {};
            for (const entry of source) {
              const key = entry.projectId || 'unassigned';
              if (!totals[key]) totals[key] = { hours: 0, amount: 0 };
              totals[key].hours += entry.duration / 3600;
              totals[key].amount += get().calculateBillableAmount(entry);
            }
            return totals;
          },

          getTotalsByClient: (entries) => {
            const source = entries ?? get().getFilteredEntries();
            const { projects } = get();
            const totals: Record<string, { hours: number; amount: number }> = {};
            for (const entry of source) {
              const project = projects.find((p) => p.id === entry.projectId);
              const key = project?.clientId || 'unassigned';
              if (!totals[key]) totals[key] = { hours: 0, amount: 0 };
              totals[key].hours += entry.duration / 3600;
              totals[key].amount += get().calculateBillableAmount(entry);
            }
            return totals;
          },
        }),
        {
          name: 'terramatrix-time-tracker',
          partialize: (state) => ({
            timer: state.timer,
            entries: state.entries,
            projects: state.projects,
            clients: state.clients,
            tasks: state.tasks,
          }),
          onRehydrateStorage: () => (state) => {
            if (state?.timer.isRunning && state.timer.startedAt) {
              const elapsed = Math.floor(
                (Date.now() - new Date(state.timer.startedAt).getTime()) / 1000
              );
              state.timer.elapsedSeconds = Math.max(0, elapsed);
            }
          },
        }
      )
    )
  )
);

// Global tick interval — keeps the timer running even when TimerBar is unmounted
let _tickInterval: ReturnType<typeof setInterval> | null = null;

function syncTimerTick() {
  const { timer } = useTimeTrackerStore.getState();
  if (timer.isRunning && timer.startedAt) {
    const elapsed = Math.floor((Date.now() - new Date(timer.startedAt).getTime()) / 1000);
    useTimeTrackerStore.setState((state) => {
      state.timer.elapsedSeconds = Math.max(0, elapsed);
    });
  }
}

useTimeTrackerStore.subscribe(
  (state) => state.timer.isRunning,
  (isRunning) => {
    if (isRunning) {
      if (!_tickInterval) _tickInterval = setInterval(syncTimerTick, 1000);
    } else {
      if (_tickInterval) { clearInterval(_tickInterval); _tickInterval = null; }
    }
  },
  { fireImmediately: true }
);

// Re-sync elapsed time when tab becomes visible (handles laptop sleep, tab switch, etc.)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncTimerTick();
  });
}
